/**
 * Roles and permissions (ARCHITECTURE_CORRECTION_PLAN §B, §L, §P; ADR 0004).
 *
 * One Bekebi Portal, one login, two roles:
 *  - admin:      the owner — full technical and operational control.
 *  - operations: John — simplified operational view and approved actions only.
 *
 * Authorisation is always enforced server-side from this map. Anything not granted is denied.
 */
export const ROLES = ['admin', 'operations'] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  // Services (service layer)
  'services.view',
  'services.manage',
  // Infrastructure layer: resources, bindings, integrations, provider references
  'infrastructure.view',
  'infrastructure.manage',
  // Renewals
  'renewals.view',
  'renewals.manage',
  // Payment Account
  'payment_account.view',
  'payment_account.manage',
  // Mailboxes
  'mail.mailboxes.view',
  'mail.mailboxes.manage', // within the Operations mailbox limit (mailbox-policy.ts)
  'mail.mailboxes.manage_unlimited', // may exceed that limit
  // Portal users
  'users.manage',
  // Activity / audit
  'audit.view', // everything
  'audit.view_own', // only the actor's own actions (conservative reading of "relevant activity")
  // Monitoring: scheduled service checks and their raw results (technical layer). Operations sees
  // only the derived per-service health on the service layer.
  'monitoring.view',
  'monitoring.manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  admin: PERMISSIONS,
  operations: [
    'services.view',
    'renewals.view',
    'payment_account.view',
    'mail.mailboxes.view',
    'mail.mailboxes.manage',
    'audit.view_own',
  ],
};

export function hasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

export interface SessionPolicy {
  /** Session ends after this long without activity. */
  idleTimeoutMs: number;
  /** Session ends this long after login, regardless of activity. */
  absoluteTimeoutMs: number;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

/** Technical defaults; tunable later. Both roles are internal staff, so neither exceeds 12 hours. */
export const SESSION_POLICY: Record<Role, SessionPolicy> = {
  admin: { idleTimeoutMs: 30 * MINUTE, absoluteTimeoutMs: 12 * HOUR },
  operations: { idleTimeoutMs: 2 * HOUR, absoluteTimeoutMs: 12 * HOUR },
};

/** Failed-login lockout: after this many consecutive failures the account is locked temporarily. */
export const LOGIN_LOCKOUT = {
  maxFailedAttempts: 5,
  lockDurationMs: 15 * MINUTE,
} as const;
